import { Modal } from "ui/Modal"
import { useTranslation } from "react-i18next"
import { Button, Card, Flex, Form, Select } from "antd"
import { Text } from "ui/Typography"
import { spacings } from "themes/variables"
import styles from "./index.module.css"
import TimeOffTypeSwitcher from "./TimeOffTypeSwitcher"
import ExistingTimeOffDetails from "./ExistingTimeOffDetails"
import PropTypes from "prop-types"
import { useDevice } from "hooks/useDevice"
import CustomDrawer from "ui/Drawer"
import { useDispatch, useSelector } from "react-redux"
import { createCalendarTimeOff, updateCalendarTimeOff } from "store/calendar"
import { getCalendarCrew } from "store/staff"
import { activeProfileSelector } from "store/user"
import { TIME_OFF_BE_KEYS } from "../../constants"
import { toaster } from "components/common/Toast"
import FirstLoading from "modules/loader-watchers/FirstLoading"
import { useEffect, useState } from "react"
import { formatDate, formatTime } from "../../utils"
import { AlertIcon } from "ui/Icon/DSIcon/AlertIcon"
import BookingConflictModal from "components/common/BookingConflictModal"

const ScheduleTimeOff = ({ open, onCancel, setIsScheduleTimeOffModalOpen, dates, onSuccess }) => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const dispatch = useDispatch()
  const { isMobile } = useDevice()
  const { timeOffSave } = useSelector((state) => state.calendar)
  const crewProfiles = useSelector((state) => state.staff?.calendarCrew || [])
  const activeProfile = useSelector(activeProfileSelector)
  const [editingTimeOffItem, setEditingTimeOffItem] = useState(null)
  const [isDirty, setIsDirty] = useState(false)
  const [conflictBookings, setConflictBookings] = useState([])
  const [conflictCrewName, setConflictCrewName] = useState("")
  const [conflictModalOpen, setConflictModalOpen] = useState(false)

  useEffect(() => {
    if (open) dispatch(getCalendarCrew())
  }, [open, dispatch])

  const buildWeekDaysPayload = (values, editingItem) => {
    const days = Object.keys(values)
      .filter((key) => key.startsWith("startTime_"))
      .map((key) => key.replace("startTime_", ""))

    const daysList = days.map((day) => ({
      day: day?.toLowerCase(),
      all_day: values?.[`fullDay_${day}`],
      start_time: formatTime(values?.[`startTime_${day}`]),
      end_time: formatTime(values?.[`endTime_${day}`])
    }))
    const isEditing = !!editingItem
    const editingDay = editingItem?.unavailable_weekday?.toLowerCase()
    const editingEntry = daysList.find((d) => d.day === editingDay) || daysList[0] || {}
    return {
      ui_type: TIME_OFF_BE_KEYS.weekDays,
      unavailable_type: TIME_OFF_BE_KEYS.weekDays,
      ...(isEditing
        ? {
            unavailable_weekday: editingEntry?.day,
            start_time: editingEntry?.start_time,
            end_time: editingEntry?.end_time,
            all_day: editingEntry?.all_day
          }
        : { days: daysList }),
      note: values?.notes,
      recurring_yearly: values?.recurringYearly ?? false,
      start_date: formatDate(values?.weeksStartDate),
      ...(values?.weeksEndDate && { end_date: formatDate(values?.weeksEndDate) })
    }
  }

  const buildDateRangePayload = (values) => ({
    ui_type: TIME_OFF_BE_KEYS.dateRange,
    unavailable_type: TIME_OFF_BE_KEYS.dateRange,
    start_date: formatDate(values?.dateRangeFromDate),
    end_date: formatDate(values?.dateRangeToDate),
    note: values?.notes,
    recurring_yearly: values?.recurringYearly ?? false,
    ...(values?.dateRangeFromTime && { start_time: formatTime(values.dateRangeFromTime) }),
    ...(values?.dateRangeToTime && { end_time: formatTime(values.dateRangeToTime) })
  })

  const buildSingleDayPayload = (values) => ({
    ui_type: TIME_OFF_BE_KEYS.singleDay,
    unavailable_type: TIME_OFF_BE_KEYS.singleDay,
    unavailable_date: formatDate(values?.singleDayDate),
    start_time: formatTime(values?.singleDayStartTime),
    end_time: formatTime(values?.singleDayEndTime),
    all_day: values?.singleDayFullDay,
    note: values?.notes,
    recurring_yearly: values?.recurringYearly ?? false
  })

  const handleApiResponse = (promise) => {
    promise
      .then((response) => {
        if (response?.message) {
          toaster.success(response.message)
        }
        form.resetFields()
        setIsDirty(false)
        setIsScheduleTimeOffModalOpen(false)
        // Call onSuccess callback to refresh bookings with proper filter parameters
        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((error) => {
        if (error?.data?.conflicting_bookings?.length > 0) {
          setConflictBookings(error.data.conflicting_bookings.map((b) => ({ ...b, id: b.booking_id })))
          setConflictCrewName(error.data.conflicting_for || "")
          setConflictModalOpen(true)
        }
      })
  }

  const onFinish = (values) => {
    const { timeOffType, crew_member_id } = values || {}
    const profileId = crew_member_id || null
    const builders = {
      [TIME_OFF_BE_KEYS.weekDays]: buildWeekDaysPayload,
      [TIME_OFF_BE_KEYS.dateRange]: buildDateRangePayload,
      [TIME_OFF_BE_KEYS.singleDay]: buildSingleDayPayload
    }
    const makePayload = builders[timeOffType]
    if (!makePayload) return
    const payload = makePayload(values, editingTimeOffItem)
    if (editingTimeOffItem) {
      handleApiResponse(dispatch(updateCalendarTimeOff(editingTimeOffItem?.id, payload, profileId)))
    } else {
      handleApiResponse(dispatch(createCalendarTimeOff(payload, profileId)))
    }
  }

  const handleCancel = () => {
    if (isDirty) {
      onCancel()
    } else {
      setIsScheduleTimeOffModalOpen(false)
    }
  }

  useEffect(() => {
    if (open) setIsDirty(false)
  }, [open])

  useEffect(() => {
    setIsDirty(false)
  }, [editingTimeOffItem])

  const crewOptions = crewProfiles.map((member) => ({
    label: member.name,
    value: member.id
  }))

  const modalContent = (
    <FirstLoading loading={timeOffSave?.loading}>
      <Flex vertical gap={spacings.L} className={styles.modalContent}>
        <Flex align="center" gap={spacings.XS} className={styles.warningContainer}>
          <AlertIcon />
          <Text>{t("calendar.schedule_time_off.timeoff_warning")}</Text>
        </Flex>
        <Form
          onFinish={onFinish}
          onValuesChange={() => setIsDirty(true)}
          form={form}
          initialValues={{
            recurringYearly: false,
            notes: "",
            crew_member_id: activeProfile?.id ?? undefined
          }}
        >
          <Form.Item name="crew_member_id" className="mb-l">
            <Select
              placeholder="Select crew member"
              options={crewOptions}
              allowClear
              showSearch
              filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
            />
          </Form.Item>
          <Card>
            <Flex vertical gap={spacings.XL}>
              <TimeOffTypeSwitcher
                form={form}
                editingTimeOffItem={editingTimeOffItem}
                setEditingTimeOffItem={setEditingTimeOffItem}
                dates={dates}
              />
            </Flex>
          </Card>
          {isMobile ? (
            <div className="mt-l">
              <ExistingTimeOffDetails form={form} setEditingTimeOffItem={setEditingTimeOffItem} />
            </div>
          ) : (
            <Card className="mt-l">
              <ExistingTimeOffDetails form={form} setEditingTimeOffItem={setEditingTimeOffItem} />
            </Card>
          )}
          <Flex gap={spacings.S} className={`mt-4xl ${styles.modalActions}`}>
            <Button variant="outlined" color="primary" onClick={handleCancel}>
              {t("global.cancel")}
            </Button>
            <Button htmlType="submit" type="primary">
              {editingTimeOffItem ? t("calendar.schedule_time_off.save_changes") : t("global.save")}
            </Button>
          </Flex>
        </Form>
      </Flex>
    </FirstLoading>
  )
  return (
    <>
      {isMobile ? (
        <CustomDrawer
          open={open}
          setOpen={setIsScheduleTimeOffModalOpen}
          onCancel={handleCancel}
          title={t("calendar.schedule_time_off.schedule_time_off")}
          width="100%"
        >
          {modalContent}
        </CustomDrawer>
      ) : (
        <Modal centered open={open} onCancel={handleCancel} title={t("calendar.schedule_time_off.schedule_time_off")} footer={null} width={800}>
          {modalContent}
        </Modal>
      )}
      <BookingConflictModal
        open={conflictModalOpen}
        onCancel={() => setConflictModalOpen(false)}
        bookings={conflictBookings}
        crewMemberName={conflictCrewName}
      />
    </>
  )
}

ScheduleTimeOff.propTypes = {
  open: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  setIsScheduleTimeOffModalOpen: PropTypes.func.isRequired,
  dates: PropTypes.object.isRequired,
  onSuccess: PropTypes.func
}

export default ScheduleTimeOff
